import { useEffect, useRef, useState } from "react";
import CabeceraPagina from "../components/CabeceraPagina.jsx";
import FormDocumentoCompra from "../components/FormDocumentoCompra.jsx";
import ModalVerificacionOCR from "../components/ModalVerificacionOCR.jsx";
import { Badge, EstadoVacio, InputBusqueda, coincideBusqueda, euros } from "../components/ui.jsx";
import { IconImprimir, IconBorrar, IconEditar } from "../components/icons.jsx";
import { imprimirDocumento } from "../utils/imprimir.js";
import { urlApiConSesion } from "../lib/sesion.js";
import { fondoSeguro } from "../lib/cierreProtegido.js";

const TONO = { pendiente_revision: "amber", validada: "green", rechazada: "red" };
const NOMBRE = { pendiente_revision: "Pendiente", validada: "Validada", rechazada: "Rechazada" };

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString("es-ES") : "—");
const nombreAlbaran = (a) => a.numero ?? a.numeroAlbaran ?? "s/n";

// Modal de conciliación: albaranes del mismo proveedor que pueden
// vincularse a la factura (los ya vinculados vienen marcados).
function ModalConciliar({ factura, onGuardado, onCerrar }) {
  const [albaranes, setAlbaranes] = useState(null);
  const [elegidos, setElegidos] = useState(() => (factura.albaranes ?? []).map((a) => a._id));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch("/api/albaranes-compra")
      .then((r) => r.json())
      .then((lista) => {
        const provId = factura.proveedor?._id ?? factura.proveedor;
        setAlbaranes(
          lista.filter(
            (a) =>
              String(a.proveedor?._id ?? a.proveedor) === String(provId) &&
              (a.estado !== "facturado" || elegidos.includes(a._id))
          )
        );
      })
      .catch(() => setAlbaranes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function alternar(id) {
    setElegidos((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));
  }

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      const r = await fetch(`/api/facturas-compra/${factura._id}/conciliar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ albaranIds: elegidos }),
      });
      const datos = await r.json();
      if (!r.ok) throw new Error(datos.error || "No se pudo conciliar");
      onGuardado();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  }

  const totalElegido = (albaranes ?? [])
    .filter((a) => elegidos.includes(a._id))
    .reduce((s, a) => s + (a.total ?? 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" {...fondoSeguro(onCerrar)}>
      <div className="modal-panel w-full max-w-lg max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-white mb-1">Conciliar albaranes</h2>
        <p className="text-sm text-slate-400 mb-4">
          Factura {factura.numeroFacturaProveedor ?? "sin nº"} de {factura.proveedor?.nombre ?? "proveedor sin asignar"}
          {" · Total "}
          <span className="text-white font-semibold">{euros(factura.total)}</span>
        </p>

        {!albaranes ? (
          <p className="text-sm text-slate-500 py-6 text-center">Cargando albaranes…</p>
        ) : albaranes.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">
            No hay albaranes de este proveedor pendientes de facturar.
          </p>
        ) : (
          <div className="space-y-2">
            {albaranes.map((a) => (
              <label
                key={a._id}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                  elegidos.includes(a._id)
                    ? "border-accent/40 bg-accent/10"
                    : "border-white/10 hover:bg-white/[0.03]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={elegidos.includes(a._id)}
                  onChange={() => alternar(a._id)}
                  className="accent-cyan-400 w-4 h-4"
                />
                <span className="flex-1">
                  <span className="text-white font-medium">{nombreAlbaran(a)}</span>
                  {a.numero && a.numeroAlbaran && (
                    <span className="text-slate-500 text-xs ml-2">prov. {a.numeroAlbaran}</span>
                  )}
                  <span className="block text-xs text-slate-500">{fecha(a.fecha)}</span>
                </span>
                <span className="text-slate-300 text-sm whitespace-nowrap">{euros(a.total)}</span>
              </label>
            ))}
          </div>
        )}

        {albaranes?.length > 0 && (
          <p className={`mt-4 text-sm ${Math.abs(totalElegido - factura.total) < 0.01 ? "text-emerald-300" : "text-amber-300"}`}>
            Albaranes elegidos: {euros(totalElegido)}
            {Math.abs(totalElegido - factura.total) >= 0.01 &&
              ` — difiere de la factura en ${euros(Math.abs(factura.total - totalElegido))}`}
          </p>
        )}

        {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onCerrar} className="btn-ghost">Cancelar</button>
          <button onClick={guardar} disabled={guardando || !albaranes} className="btn-primary">
            {guardando ? "Guardando…" : "Guardar conciliación"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal "Importar del correo": lista los mensajes del buzón entrante de la
// empresa (Ajustes → Correo) que traen adjuntos PDF o imagen y manda el
// adjunto elegido al mismo OCR que usa "Importar PDF (IA)".
function ModalCorreo({ onImportado, onCerrar }) {
  const [mensajes, setMensajes] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [importando, setImportando] = useState(null); // "uid:indice"

  async function cargarMensajes(texto = busqueda) {
    setCargando(true);
    setError(null);
    try {
      const params = new URLSearchParams({ soloAdjuntos: "1", limite: "30" });
      if (texto.trim()) params.set("busqueda", texto.trim());
      const r = await fetch(`/api/correo/entrante/mensajes?${params}`);
      const datos = await r.json();
      if (!r.ok) throw new Error(datos.error || "No se pudo leer el buzón");
      setMensajes(datos);
    } catch (e) {
      setError(e.message);
      setMensajes([]);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargarMensajes("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function importarAdjunto(m, indice, nombre) {
    setImportando(`${m.uid}:${indice}`);
    setError(null);
    try {
      const r = await fetch(
        `/api/correo/entrante/mensajes/${m.uid}/adjunto?carpeta=${encodeURIComponent(m.carpeta ?? "INBOX")}&indice=${indice}`
      );
      if (!r.ok) throw new Error((await r.json()).error || "No se pudo bajar el adjunto");
      const blob = await r.blob();
      const fichero = new File([blob], nombre || "factura.pdf", {
        type: blob.type || "application/pdf",
      });
      const fd = new FormData();
      fd.append("documento", fichero);
      const rOcr = await fetch("/api/facturas-compra/ocr", { method: "POST", body: fd });
      const datos = await rOcr.json();
      if (!rOcr.ok) throw new Error(datos.error || "Error al procesar el documento");
      onImportado(datos);
    } catch (e) {
      setError(e.message);
    } finally {
      setImportando(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" {...fondoSeguro(onCerrar)}>
      <div className="modal-panel w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-white mb-1">Importar del correo</h2>
        <p className="text-sm text-slate-400 mb-4">
          Mensajes recientes del buzón de la empresa con adjuntos PDF o imagen. Elige uno y se procesará con IA.
        </p>

        <div className="flex gap-2 mb-4">
          <InputBusqueda
            value={busqueda}
            onChange={setBusqueda}
            placeholder="Buscar por remitente o asunto…"
          />
          <button className="btn-ghost whitespace-nowrap" onClick={() => cargarMensajes()} disabled={cargando}>
            {cargando ? "Buscando…" : "Buscar"}
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-400 mb-4">
            {error}
            {/buzón entrante configurado/i.test(error) && (
              <span className="block text-slate-400 mt-1">Configúralo en Ajustes → Correo → Buzón entrante.</span>
            )}
          </p>
        )}

        {!mensajes ? (
          <p className="text-sm text-slate-500 py-6 text-center">Leyendo el buzón…</p>
        ) : mensajes.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">
            {cargando ? "Leyendo el buzón…" : "No hay correos con adjuntos válidos."}
          </p>
        ) : (
          <div className="space-y-2">
            {mensajes.map((m) => (
              <div key={m.uid} className="rounded-xl border border-white/10 px-4 py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-white font-medium truncate" title={m.de}>
                    {m.deNombre || m.de || "(desconocido)"}
                  </span>
                  <span className="text-xs text-slate-500 whitespace-nowrap num">
                    {m.fecha ? new Date(m.fecha).toLocaleDateString("es-ES") : ""}
                  </span>
                </div>
                <p className="text-sm text-slate-400 truncate" title={m.asunto}>{m.asunto}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {m.adjuntos.map((a, i) => (
                    <button
                      key={`${m.uid}-${i}`}
                      onClick={() => importarAdjunto({ ...m, carpeta: m.carpeta }, i, a.nombre)}
                      disabled={importando !== null}
                      className="btn-ghost text-xs"
                      title={a.nombre}
                    >
                      {importando === `${m.uid}:${i}` ? "Procesando…" : a.nombre}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onCerrar} className="btn-ghost">Cerrar</button>
        </div>
      </div>
    </div>
  );
}

export default function ComprasFacturasPage() {
  const [lista, setLista] = useState(null);
  const [q, setQ] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todas");
  const [filtroPago, setFiltroPago] = useState("todos");
  const [filtroOrigen, setFiltroOrigen] = useState("todas");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [formManual, setFormManual] = useState(false);
  const [editando, setEditando] = useState(null);
  const [conciliando, setConciliando] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [verificando, setVerificando] = useState(null); // { tipo, documento }
  const [delCorreo, setDelCorreo] = useState(false);
  const inputRef = useRef(null);

  // Filtra por texto (todos los campos visibles) + estado, pago, origen y
  // rango de fechas. Todo en cliente: el servidor devuelve como máximo las
  // 200 más recientes.
  const filtrada = (lista ?? []).filter((f) => {
    if (filtroEstado !== "todas" && f.estado !== filtroEstado) return false;
    if (filtroPago === "pendientes" && !(f.estado === "validada" && f.estadoPago !== "pagada")) return false;
    if (filtroPago === "parcial" && f.estadoPago !== "parcial") return false;
    if (filtroPago === "pagada" && f.estadoPago !== "pagada") return false;
    if (filtroOrigen !== "todas" && (f.origen ?? "manual") !== filtroOrigen) return false;
    const fIso = f.fechaExpedicion ? new Date(f.fechaExpedicion).toISOString().slice(0, 10) : null;
    if (desde && (!fIso || fIso < desde)) return false;
    if (hasta && (!fIso || fIso > hasta)) return false;
    return coincideBusqueda(
      q,
      f.numeroFacturaProveedor,
      f.proveedor?.nombre,
      f.proveedor?.nif,
      fecha(f.fechaExpedicion),
      f.origen === "ocr" ? "ia" : "manual",
      (f.albaranes ?? []).map((a) => nombreAlbaran(a)).join(" "),
      euros(f.total),
      f.total,
      NOMBRE[f.estado]
    );
  });

  async function cargar() {
    try {
      const r = await fetch("/api/facturas-compra");
      const datos = await r.json();
      if (!r.ok) throw new Error(datos.error || "Error al cargar");
      setLista(datos);
    } catch (e) {
      setError(e.message);
      setLista([]);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  async function importarPdf(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setSubiendo(true);
    setError(null);
    setAviso(null);
    try {
      const fd = new FormData();
      fd.append("documento", f);
      const r = await fetch("/api/facturas-compra/ocr", { method: "POST", body: fd });
      const datos = await r.json();
      if (!r.ok) throw new Error(datos.error || "Error al procesar el documento");
      // Al terminar la importación se abre la verificación del documento.
      setVerificando(datos);
      await cargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubiendo(false);
    }
  }

  async function validar(f) {
    const r = await fetch(`/api/facturas-compra/${f._id}/validar`, { method: "POST" });
    if (r.ok) cargar();
    else alert((await r.json()).error || "No se pudo validar");
  }

  async function borrar(f) {
    const avisos = [];
    if (f.estado === "validada" && !(f.albaranes ?? []).length) {
      avisos.push("se devolverá la entrada de stock que hizo");
    }
    if ((f.albaranes ?? []).length) avisos.push("los albaranes conciliados quedarán liberados");
    if ((f.pagos ?? []).length) avisos.push("también se borrarán sus pagos registrados");
    const extra = avisos.length ? `\n\nOjo: ${avisos.join("; ")}.` : "";
    if (!window.confirm(`¿Borrar la factura ${f.numeroFacturaProveedor ?? ""}?${extra}`)) return;
    const r = await fetch(`/api/facturas-compra/${f._id}`, { method: "DELETE" });
    if (r.ok) cargar();
    else alert((await r.json()).error || "No se pudo borrar");
  }

  return (
    <>
      <CabeceraPagina
        titulo="Facturas de compra"
        descripcion="Facturas recibidas de proveedores: por IA, a mano o desde albaranes. Concilialas con sus albaranes."
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={importarPdf}
        />
        <button onClick={() => window.print()} className="btn-ghost">
          Imprimir
        </button>
        <button onClick={() => inputRef.current?.click()} disabled={subiendo} className="btn-ghost">
          {subiendo ? "Procesando…" : "Importar PDF (IA)"}
        </button>
        <button onClick={() => setDelCorreo(true)} className="btn-ghost">
          Del correo
        </button>
        <button onClick={() => setFormManual(true)} className="btn-primary">
          Nueva manual
        </button>
      </CabeceraPagina>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</div>
      )}
      {aviso && (
        <div className="mb-4 rounded-xl border border-accent/30 bg-accent/10 px-4 py-3 text-sm text-accent">{aviso}</div>
      )}

      {lista?.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <InputBusqueda
            value={q}
            onChange={setQ}
            placeholder="Buscar por nº, proveedor, NIF, total…"
          />
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className="input">
            <option value="todas">Estado: todas</option>
            <option value="pendiente_revision">Pendientes de revisión</option>
            <option value="validada">Validadas</option>
            <option value="rechazada">Rechazadas</option>
          </select>
          <select value={filtroPago} onChange={(e) => setFiltroPago(e.target.value)} className="input">
            <option value="todos">Pago: todos</option>
            <option value="pendientes">Pendientes de pago</option>
            <option value="parcial">Pago parcial</option>
            <option value="pagada">Pagadas</option>
          </select>
          <select value={filtroOrigen} onChange={(e) => setFiltroOrigen(e.target.value)} className="input">
            <option value="todas">Origen: todos</option>
            <option value="ocr">IA</option>
            <option value="manual">Manual</option>
          </select>
          <label className="flex items-center gap-1.5 text-sm text-slate-500">
            Desde
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="input" />
          </label>
          <label className="flex items-center gap-1.5 text-sm text-slate-500">
            Hasta
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="input" />
          </label>
          {(q || filtroEstado !== "todas" || filtroPago !== "todos" || filtroOrigen !== "todas" || desde || hasta) && (
            <button
              onClick={() => {
                setQ("");
                setFiltroEstado("todas");
                setFiltroPago("todos");
                setFiltroOrigen("todas");
                setDesde("");
                setHasta("");
              }}
              className="btn-ghost text-xs"
            >
              Limpiar filtros
            </button>
          )}
          <span className="text-xs text-slate-500 ml-auto">
            {filtrada.length} de {lista.length}
          </span>
        </div>
      )}

      <div className="panel px-3.5 py-2">
        {!lista ? null : lista.length === 0 ? (
          <EstadoVacio
            titulo="Sin facturas de compra"
            descripcion="Importa el PDF de una factura con IA, dala de alta a mano o convierte albaranes: aquí quedará el histórico."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Nº proveedor</th>
                  <th>Proveedor</th>
                  <th>Fecha</th>
                  <th>Vence</th>
                  <th>Origen</th>
                  <th>Conciliación</th>
                  <th className="text-right">Ret. IRPF</th>
                  <th className="text-right">Total</th>
                  <th>Divisa</th>
                  <th>Estado</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtrada.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center text-slate-500 py-8">
                      Ninguna factura cumple esos filtros.
                    </td>
                  </tr>
                )}
                {filtrada.map((f) => {
                  const sumaAlbaranes = (f.albaranes ?? []).reduce((s, a) => s + (a.total ?? 0), 0);
                  // Vencimiento: el real si está guardado; si no, estimado a
                  // 30 días (marcado con ~). Rojo si está vencida y sin pagar.
                  const sinPagar = f.estadoPago !== "pagada" && f.estado === "validada";
                  const vencCalc = (() => {
                    if (f.vencimiento) return { d: new Date(f.vencimiento), aprox: false };
                    const d = new Date(f.fechaExpedicion ?? f.createdAt ?? Date.now());
                    d.setDate(d.getDate() + 30);
                    return { d, aprox: true };
                  })();
                  const dias = Math.ceil((vencCalc.d - Date.now()) / 86400000);
                  const vencida = sinPagar && dias < 0;
                  const cerca = sinPagar && dias >= 0 && dias <= 7;
                  const conciliacion =
                    (f.albaranes ?? []).length === 0
                      ? null
                      : Math.abs(sumaAlbaranes - (f.total ?? 0)) < 0.01
                        ? { nombre: "Coincide", tono: "green" }
                        : { nombre: "Revisar", tono: "amber" };
                  return (
                  <tr key={f._id}>
                    <td className="font-bold text-white whitespace-nowrap num">
                      {f.numeroFacturaProveedor ?? "—"}
                    </td>
                    <td className="text-slate-300 max-w-[190px]">
                      <span className="block truncate" title={f.proveedor?.nombre}>{f.proveedor?.nombre ?? "—"}</span>
                    </td>
                    <td className="text-slate-400 num">{fecha(f.fechaExpedicion)}</td>
                    <td className="whitespace-nowrap num">
                      {vencida ? (
                        <span className="font-semibold text-rose-400" title={`Vencida hace ${-dias} día(s)`}>
                          {vencCalc.d.toLocaleDateString("es-ES")}
                        </span>
                      ) : cerca ? (
                        <span className="font-semibold text-amber-300" title={`Vence en ${dias} día(s)`}>
                          {vencCalc.d.toLocaleDateString("es-ES")}
                        </span>
                      ) : (
                        <span
                          className={sinPagar ? "text-slate-400" : "text-slate-600"}
                          title={vencCalc.aprox ? "Estimado: fecha + 30 días (la factura no indica vencimiento)" : ""}
                        >
                          {vencCalc.aprox ? "~" : ""}
                          {vencCalc.d.toLocaleDateString("es-ES")}
                        </span>
                      )}
                    </td>
                    <td>
                      <Badge tono={f.origen === "ocr" ? "cyan" : "slate"}>
                        {f.origen === "ocr" ? "IA" : "Manual"}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap">
                      {conciliacion ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Badge tono={conciliacion.tono}>{conciliacion.nombre}</Badge>
                          <span className="text-[0.6875rem] text-slate-500 num">
                            {f.albaranes.map((a) => nombreAlbaran(a)).join(", ")}
                          </span>
                        </span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="text-right whitespace-nowrap num">
                      {(f.retencionIrpf?.importe ?? 0) > 0 ? (
                        <span className="text-amber-300" title={`Retención ${f.retencionIrpf.porcentaje}% sobre la base · Modelo ${f.retencionIrpf.modelo}`}>
                          −{euros(f.retencionIrpf.importe)}
                          <span className="text-xs text-slate-500 ms-1">({f.retencionIrpf.porcentaje}%)</span>
                        </span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="text-right text-slate-300 whitespace-nowrap num">
                      {euros(f.total)}
                      {(f.retencionIrpf?.importe ?? 0) > 0 && (
                        <span className="block text-[0.6875rem] text-slate-500">a pagar al proveedor</span>
                      )}
                    </td>
                    <td className="text-center whitespace-nowrap">
                      {f.divisa === "USD" ? (
                        <span className="text-slate-300" title={`Tipo de cambio ${f.tipoCambio} · Total en factura $${(f.totalDivisa ?? 0).toFixed(2)}`}>
                          USD
                        </span>
                      ) : (
                        <span className="text-slate-500">EUR</span>
                      )}
                    </td>
                    <td>
                      <Badge tono={TONO[f.estado]}>{NOMBRE[f.estado] ?? f.estado}</Badge>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        onClick={() =>
                          imprimirDocumento({
                            tipo: "Factura de compra",
                            numero: f.numero ?? f.numeroProveedor,
                            fecha: f.fecha,
                            contraparte: f.proveedor,
                            quienContraparte: "Proveedor",
                            lineas: f.lineas,
                            notas: f.notas,
                            retencionIrpf: f.retencionIrpf,
                            totalReal: f.total,
                          })
                        }
                        title="Imprimir"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-accent hover:bg-accent/10 transition-colors align-middle mr-2"
                      >
                        <IconImprimir />
                      </button>
                      {f.ocr?.ficheroUrl && (
                        <a
                          href={urlApiConSesion(f.ocr.ficheroUrl)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-accent hover:underline mr-3"
                        >
                          PDF
                        </a>
                      )}
                      {f.estado !== "rechazada" && (
                        <button
                          onClick={() => setConciliando(f)}
                          className="text-xs text-amber-300 hover:underline mr-3"
                        >
                          Conciliar
                        </button>
                      )}
                      {f.estado === "pendiente_revision" && (
                        <button
                          onClick={() => validar(f)}
                          className="text-xs text-emerald-300 hover:underline mr-3"
                        >
                          Validar
                        </button>
                      )}
                      {f.estado !== "rechazada" && (
                        <button
                          onClick={() => setEditando(f)}
                          title="Editar factura"
                          className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-accent hover:bg-accent/10 transition-colors align-middle mr-2"
                        >
                          <IconEditar />
                        </button>
                      )}
                      <button
                        onClick={() => borrar(f)}
                        title="Borrar factura"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors align-middle"
                      >
                        <IconBorrar />
                      </button>
                    </td>
                  </tr>
                  );
                })}
                {filtrada.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center text-slate-500 py-8">
                      Sin resultados para «{q}».
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formManual && (
        <FormDocumentoCompra
          titulo="Nueva factura de compra (manual)"
          url="/api/facturas-compra"
          conNumeroProveedor
          conTotalReal
          etiquetaNumero="Nº factura del proveedor"
          campoNumero="numeroFacturaProveedor"
          onGuardado={() => { setFormManual(false); cargar(); }}
          onCerrar={() => setFormManual(false)}
        />
      )}
      {editando && (
        <FormDocumentoCompra
          titulo={`Editar factura ${editando.numeroFacturaProveedor ?? ""}`}
          url={`/api/facturas-compra/${editando._id}`}
          metodo="PUT"
          conNumeroProveedor
          conTotalReal
          etiquetaNumero="Nº factura del proveedor"
          campoNumero="numeroFacturaProveedor"
          inicial={{
            proveedor: editando.proveedor?._id ?? editando.proveedor ?? "",
            fecha: editando.fechaExpedicion
              ? new Date(editando.fechaExpedicion).toISOString().slice(0, 10)
              : new Date().toISOString().slice(0, 10),
            vencimiento: editando.vencimiento ?? null,
            numeroFacturaProveedor: editando.numeroFacturaProveedor ?? "",
            notas: editando.notas ?? "",
            lineas: editando.lineas ?? [],
            total: editando.total,
            divisa: editando.divisa ?? "EUR",
            tipoCambio: editando.tipoCambio ?? 1,
            crearArticulos: editando.crearArticulos,
            retencionIrpf: editando.retencionIrpf,
          }}
          onGuardado={() => { setEditando(null); cargar(); }}
          onCerrar={() => setEditando(null)}
        />
      )}
      {conciliando && (
        <ModalConciliar
          factura={conciliando}
          onGuardado={() => { setConciliando(null); cargar(); }}
          onCerrar={() => setConciliando(null)}
        />
      )}
      {delCorreo && (
        <ModalCorreo
          onImportado={(datos) => {
            setDelCorreo(false);
            setVerificando(datos);
            cargar();
          }}
          onCerrar={() => setDelCorreo(false)}
        />
      )}
      {verificando && (
        <ModalVerificacionOCR
          resultado={verificando}
          onAceptado={(msg) => {
            setVerificando(null);
            setAviso(msg);
            cargar();
          }}
          onCerrar={() => {
            setVerificando(null);
            cargar();
          }}
        />
      )}
    </>
  );
}
